import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import profile from "../../../../images/DemoIm.png";
import rankFallback from "../../../../images/icon/rank.png";
import { GlobalSet } from "../../global/GlobalProvider";
import { useTranslation } from "react-i18next";

const API_KEY = "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";
const RANK_NAMES = ["User", "Associate", "Executive Associate", "Senior Executive", "Team Leader", "Senior Team Leader", "Manager", "Senior Manager", "Director", "Regional Director", "National Director", "Vice President", "Senior Vice President", "Crown Leader", "Royal Ambassador", "Global Master"];
const safeValue = (value) => value === null || value === undefined || value === "" ? "—" : String(value);
const amountValue = (value) => `$${value === null || value === undefined || value === "" ? "0" : value}`;

const AppProfile = () => {
  const { BaseUrl } = GlobalSet();
  const { t } = useTranslation();
  const [user, setUser] = useState({});
  const [preview, setPreview] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const fileInputRef = useRef(null);
  const savingRef = useRef(false);
  const previewUrlRef = useRef("");

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    if (localStorage.getItem("auth_token") !== null) {
      axios.post("/apps-profile", {
        key_s: API_KEY,
        currency: localStorage.getItem("selectedCurrency"),
      }, { signal: controller.signal }).then(({ data }) => {
        if (active) setUser(data?.data || {});
      }).catch((error) => {
        if (active && error?.code !== "ERR_CANCELED") console.error("Profile request failed:", error);
      });
    }
    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const imageUrl = useMemo(() => {
    if (!user.picture_Url || user.picture_Url === "0") return profile;
    return `${BaseUrl || ""}${user.picture_Url}`;
  }, [BaseUrl, user.picture_Url]);

  const onChoosePhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      swal("Invalid file", "Please select an image.", "warning");
      event.target.value = "";
      return;
    }
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setPreview(url);
    setSelectedFile(file);
    setValidationErrors({});
  };

  const onCancelPhoto = () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = "";
    setPreview("");
    setSelectedFile(null);
    setValidationErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submitProduct = async (event) => {
    event.preventDefault();
    if (!selectedFile || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("key_s", API_KEY);
      formData.append("your_image", selectedFile);
      const { data } = await axios.post("/change-profile-photo", formData);
      if (data?.status === "success") {
        // Refresh the profile after upload to use the server-confirmed image URL.
        try {
          const updated = await axios.post("/apps-profile", {
            key_s: API_KEY,
            currency: localStorage.getItem("selectedCurrency"),
          });
          setUser(updated.data?.data || {});
        } catch (refreshError) {
          console.error("Profile refresh failed:", refreshError);
        }
        onCancelPhoto();
        swal("Success", data.message || "Profile photo updated.", "success");
      } else if (data?.status === "fail") {
        swal("Warning", data.message || "Unable to update profile photo.", "warning");
      } else {
        setValidationErrors(data?.validation_error || {});
      }
    } catch (error) {
      swal("Error", error?.response?.data?.message || "Unable to upload photo. Please try again.", "error");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const rankNumber = Number(user.clubstatus);
  const rankName = RANK_NAMES[rankNumber] || "Unknown";
  const packageLabel = Number(user.total_invest || 0) >= 100 ? "Standard Package" : "General Package";
  const isActive = Number(user.active_status) === 1;
  const walletCards = [
    { title: t("pro_pg_curr_bal"), value: amountValue(user.balance), icon: "fa-solid fa-wallet", light: true },
    { title: t("pro_pg_tot_dep"), value: amountValue(user.total_deposit), icon: "fa-solid fa-arrow-down", light: false },
    { title: t("pro_pg_tot_inc"), value: amountValue(user.total_Income), icon: "fa-solid fa-chart-line", light: false },
    { title: t("pro_pg_tot_wit"), value: amountValue(user.total_withdraw), icon: "fa-solid fa-arrow-up-right-from-square", light: true },
  ];
  const profileFields = [
    { label: t("pro_pg_name"), value: user.name, icon: "fa-regular fa-user" },
    { label: "Last Name", value: user.last_name, icon: "fa-regular fa-id-card" },
    { label: t("pro_pg_email"), value: user.email, icon: "fa-regular fa-envelope" },
    { label: "Mobile", value: user.mobile_No, icon: "fa-solid fa-phone" },
    { label: t("pro_pg_location"), value: user.address, icon: "fa-solid fa-location-dot" },
    { label: "Join Date", value: user.created_at, icon: "fa-regular fa-calendar" },
  ];

  return (
    <div className="rm-profile-root">
      <style>{`
        html,body,#root {background:#00191D!important;}
        .rm-profile-root{--navy:#00191D;--cyan:#16B8C7;--cyan2:#42E0E4;--white:#fff;--muted:#a9c1c5;--line:#204449;min-height:100vh;width:100%;background:#00191D;color:#fff;padding:12px 4px 28px;font-size:14px;overflow-x:clip}
        .rm-profile-root *{box-sizing:border-box}
        .rm-profile-root button,.rm-profile-root input{font:inherit}
        .rm-profile-container{width:100%;max-width:none;margin:0 auto}
        .rm-profile-header{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:15px 17px;margin-bottom:12px;border:1px solid #265158;border-radius:16px;background:#05262B}
        .rm-profile-kicker{font-size:11px;letter-spacing:.13em;text-transform:uppercase;color:#7BDCE3;font-weight:800;margin-bottom:4px}
        .rm-profile-header h1{font-size:21px;font-weight:800;margin:0;color:#fff}
        .rm-profile-header-tag{color:#00191D;background:#d7f9f9;border-radius:999px;padding:7px 11px;font-size:12px;font-weight:800;white-space:nowrap}
        .rm-profile-hero{display:grid;grid-template-columns:minmax(0,1.45fr) minmax(260px,.55fr);gap:12px;margin-bottom:12px}
        .rm-profile-main{border:1px solid #28545a;border-radius:18px;background:linear-gradient(115deg,#062c32,#00191D);padding:23px;min-width:0;position:relative;overflow:hidden}
        .rm-profile-main:after{content:"";position:absolute;width:170px;height:170px;right:-85px;top:-95px;border:1px solid rgba(22,184,199,.22);border-radius:50%;pointer-events:none}
        .rm-profile-identity{display:flex;align-items:center;gap:20px;min-width:0;position:relative;z-index:1}
        .rm-profile-avatar-area{text-align:center;flex-shrink:0}
        .rm-profile-avatar{width:110px;height:110px;border:3px solid #16B8C7;border-radius:22px;object-fit:cover;background:#fff;display:block}
        .rm-profile-avatar-action{position:relative;margin-top:9px;display:inline-flex;align-items:center;gap:6px;border:1px solid #417078;border-radius:9px;padding:6px 9px;color:#fff;cursor:pointer;font-size:12px}
        .rm-profile-avatar-action input{position:absolute;inset:0;opacity:0;cursor:pointer;width:100%}
        .rm-profile-username{margin:0 0 5px;color:#fff;font-size:clamp(19px,2.4vw,29px);font-weight:850;overflow-wrap:anywhere}
        .rm-profile-subtitle{color:#a9c1c5;margin:0 0 12px}
        .rm-profile-pills{display:flex;flex-wrap:wrap;gap:7px}
        .rm-profile-pill{border:1px solid #2b555b;border-radius:9px;padding:7px 10px;font-size:12px;color:#d7eff0;background:#10353a}
        .rm-profile-pill i{color:#57d4df;margin-right:5px}
        .rm-profile-pill.active{color:#71e3b4;border-color:#26725c}
        .rm-profile-pill.inactive{color:#ffcf84;border-color:#805e2f}
        .rm-profile-actions{display:flex;gap:8px;align-items:center;justify-content:center;margin-top:9px;flex-wrap:wrap}
        .rm-profile-btn{border:0;border-radius:10px;padding:10px 14px;cursor:pointer;font-weight:800;background:linear-gradient(110deg,#16B8C7,#48E2E2);color:#00191D;transition:transform .18s ease,filter .18s ease}
        .rm-profile-btn:hover:not(:disabled){transform:translateY(-2px);filter:brightness(1.07)}
        .rm-profile-btn:disabled{opacity:.6;cursor:not-allowed}
        .rm-profile-btn-ghost{background:#17383d;border:1px solid #315c62;color:#fff}
        .rm-profile-error{color:#ffb0a9;font-size:12px;margin-top:6px}
        .rm-profile-rank{border-radius:18px;background:#fff;color:#09292d;padding:20px;display:flex;align-items:center;justify-content:space-between;gap:12px;min-width:0}
        .rm-profile-rank small{display:block;font-size:12px;color:#5d777b;margin-bottom:5px;font-weight:700}
        .rm-profile-rank strong{font-size:20px;line-height:1.25;display:block;overflow-wrap:anywhere}
        .rm-profile-rank img{width:72px;height:72px;object-fit:contain;flex-shrink:0}
        .rm-profile-rank .rm-profile-rank-num{font-size:12px;color:#007c8c;font-weight:800;margin-top:10px}
        .rm-profile-wallets{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin-bottom:12px}
        .rm-profile-wallet{min-width:0;border:1px solid #265157;border-radius:16px;padding:18px;background:#05272d;display:flex;align-items:flex-start;gap:12px;transition:transform .18s ease,border-color .18s ease}
        .rm-profile-wallet:hover{transform:translateY(-2px);border-color:#16B8C7}
        .rm-profile-wallet.light{background:#fff;border-color:#fff;color:#07262b}
        .rm-profile-wallet-icon{width:42px;height:42px;display:grid;place-items:center;flex-shrink:0;border-radius:12px;background:linear-gradient(135deg,#16B8C7,#50e0e0);color:#00191D;font-size:17px}
        .rm-profile-wallet-label{font-size:12px;color:#a9c1c5;margin:0 0 6px}
        .rm-profile-wallet.light .rm-profile-wallet-label{color:#627b80}
        .rm-profile-wallet-value{font-size:clamp(16px,1.7vw,23px);font-weight:850;line-height:1.2;overflow-wrap:anywhere;color:#fff;margin:0}
        .rm-profile-wallet.light .rm-profile-wallet-value{color:#07262b}
        .rm-profile-info{border:1px solid #285057;border-radius:18px;background:#06272c;padding:19px}
        .rm-profile-info-heading{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:14px}
        .rm-profile-info-heading h2{margin:0;font-size:18px;font-weight:800;color:#fff}
        .rm-profile-info-heading h2 i{color:#16B8C7;margin-right:8px}
        .rm-profile-info-heading span{font-size:12px;color:#b5d1d5}
        .rm-profile-info-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
        .rm-profile-info-item{background:#fff;color:#07262b;border-radius:12px;padding:14px;min-width:0;border:1px solid #d8ebec}
        .rm-profile-info-label{font-size:12px;color:#5c767b;display:flex;gap:7px;align-items:center;margin-bottom:7px}
        .rm-profile-info-label i{color:#098e9b}
        .rm-profile-info-value{font-size:14px;font-weight:750;overflow-wrap:anywhere;color:#082a2e}
        @media(max-width:1000px){.rm-profile-hero{grid-template-columns:1fr}.rm-profile-wallets{grid-template-columns:repeat(2,minmax(0,1fr))}.rm-profile-info-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
        @media(max-width:600px){.rm-profile-header{padding:12px}.rm-profile-header h1{font-size:17px}.rm-profile-header-tag{font-size:10px}.rm-profile-main{padding:15px}.rm-profile-identity{flex-direction:column;text-align:center;gap:12px}.rm-profile-pills{justify-content:center}.rm-profile-rank{padding:15px}.rm-profile-wallets{gap:8px}.rm-profile-wallet{padding:12px;flex-direction:column;gap:9px}.rm-profile-wallet-icon{width:36px;height:36px;font-size:15px}.rm-profile-wallet-value{font-size:17px}.rm-profile-info{padding:12px}.rm-profile-info-grid{grid-template-columns:1fr;gap:8px}.rm-profile-info-item{padding:12px}}
        @media(prefers-reduced-motion:reduce){.rm-profile-root *, .rm-profile-root *:before,.rm-profile-root *:after{animation:none!important;transition:none!important}}
      `}</style>

      <div className="rm-profile-container">
        <header className="rm-profile-header">
          <div>
            <div className="rm-profile-kicker">RM CROWN MARKET</div>
            <h1>My Profile</h1>
          </div>
          <span className="rm-profile-header-tag">Account Overview</span>
        </header>

        <section className="rm-profile-hero">
          <div className="rm-profile-main">
            <div className="rm-profile-identity">
              <form className="rm-profile-avatar-area" onSubmit={submitProduct} encType="multipart/form-data">
                <img className="rm-profile-avatar" src={preview || imageUrl} alt="Profile" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = profile; }} />
                <label className="rm-profile-avatar-action">
                  <i className="fa-solid fa-camera" /> Change Photo
                  <input ref={fileInputRef} type="file" name="your_image" accept="image/*" onChange={onChoosePhoto} />
                </label>
                {selectedFile && (
                  <div className="rm-profile-actions">
                    <button className="rm-profile-btn" type="submit" disabled={saving}>{saving ? t("Submitting") : t("Submit")}</button>
                    <button className="rm-profile-btn rm-profile-btn-ghost" type="button" onClick={onCancelPhoto} disabled={saving}>Cancel</button>
                  </div>
                )}
                {validationErrors.your_image && <div className="rm-profile-error">{String(validationErrors.your_image)}</div>}
              </form>
              <div>
                <p className="rm-profile-subtitle">{t("pro_pg_username")}</p>
                <h2 className="rm-profile-username">{safeValue(user.username)}</h2>
                <div className="rm-profile-pills">
                  <span className="rm-profile-pill"><i className="fa-solid fa-user-tag" /> User ID: {safeValue(user.reference_code)}</span>
                  <span className="rm-profile-pill"><i className="fa-solid fa-box" /> {packageLabel}</span>
                  <span className={`rm-profile-pill ${isActive ? "active" : "inactive"}`}><i className={isActive ? "fa-solid fa-circle-check" : "fa-solid fa-clock"} /> {isActive ? "Active" : "Inactive"}</span>
                </div>
              </div>
            </div>
          </div>
          <aside className="rm-profile-rank">
            <div>
              <small>Partner Rank</small>
              <strong>{rankName}</strong>
              <div className="rm-profile-rank-num">Rank Level: {Number.isFinite(rankNumber) && user.clubstatus !== undefined ? rankNumber : "—"}</div>
            </div>
            <img src={`${BaseUrl || ""}public/basic_icon/rank${user.clubstatus ?? 0}.png`} alt="Rank badge" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = rankFallback; }} />
          </aside>
        </section>

        <section className="rm-profile-wallets" aria-label="Wallet overview">
          {walletCards.map((card) => (
            <article className={`rm-profile-wallet ${card.light ? "light" : ""}`} key={card.title}>
              <div className="rm-profile-wallet-icon"><i className={card.icon} /></div>
              <div>
                <p className="rm-profile-wallet-label">{card.title}</p>
                <p className="rm-profile-wallet-value">{card.value}</p>
              </div>
            </article>
          ))}
        </section>

        <section className="rm-profile-info">
          <div className="rm-profile-info-heading">
            <h2><i className="fa-regular fa-address-card" /> {t("pro_pg_ab_me")}</h2>
            <span>Personal Details</span>
          </div>
          <div className="rm-profile-info-grid">
            {profileFields.map((field, index) => (
              <div className="rm-profile-info-item" key={index}>
                <div className="rm-profile-info-label"><i className={field.icon} />{field.label}</div>
                <div className="rm-profile-info-value">{safeValue(field.value)}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AppProfile;
